/**
 * Messages for a caller who cannot use CDR yet, each with a "Return to sign in" action that signs
 * the caller out: a full-page sign-in error and an in-page "access not configured" notice.
 */

import type { ReactNode } from "react"
import Alert from "@cloudscape-design/components/alert"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Container from "@cloudscape-design/components/container"
import ContentLayout from "@cloudscape-design/components/content-layout"
import Header from "@cloudscape-design/components/header"
import SpaceBetween from "@cloudscape-design/components/space-between"
import { useState } from "react"
import { useAuth } from "../../contexts/AuthContext"

/**
 * Center `children` in the viewport at a readable width, for pages shown outside the app layout.
 */
export const CenteredPage = ({ children }: { children: ReactNode }) => (
  <div
    style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}
  >
    <div style={{ width: "100%", maxWidth: "480px" }}>{children}</div>
  </div>
)

/**
 * Sign the caller out, and show an alert above the action when signing out fails.
 */
const ReturnToSignIn = ({ align }: { align: "left" | "center" }) => {
  const { logout } = useAuth()
  const [returnError, setReturnError] = useState<string | null>(null)

  const handleReturnToSignIn = async () => {
    setReturnError(null)
    try {
      await logout()
    } catch (error) {
      console.error("Return to sign in failed:", error)
      setReturnError(
        "We could not sign you out. Please clear your browser cookies and go to the sign-in page manually.",
      )
    }
  }

  return (
    <SpaceBetween size="l">
      {returnError && <Alert type="error">{returnError}</Alert>}
      <Box textAlign={align}>
        <Button variant="primary" onClick={() => void handleReturnToSignIn()}>
          Return to sign in
        </Button>
      </Box>
    </SpaceBetween>
  )
}

/**
 * Full-page sign-in error with a "Return to sign in" action, for the bare auth callback page.
 */
export const SignInMessage = ({ header, body }: { header: string; body: string }) => (
  <CenteredPage>
    <Container>
      <SpaceBetween size="l">
        <Alert type="error" header={header}>
          {body}
        </Alert>
        <ReturnToSignIn align="center" />
      </SpaceBetween>
    </Container>
  </CenteredPage>
)

/**
 * Page content telling a signed-in caller whose access resolves to no page that their access is not
 * configured. Renders inside the app layout.
 */
export const AccessNotConfigured = () => (
  <ContentLayout header={<Header variant="h1">Access not configured</Header>}>
    <Container>
      <SpaceBetween size="l">
        <Box variant="p">
          You are signed in, but your account has no access in CDR yet. Ask your administrator to
          grant you access, then sign in again.
        </Box>
        <ReturnToSignIn align="left" />
      </SpaceBetween>
    </Container>
  </ContentLayout>
)
